import { useState, useEffect, useMemo } from 'react';
import { Menu, X } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import FullLogo from '../assets/ROILogo.png';
import { useAppData } from '../contexts/AppDataContext';
import { toTitleCase } from '@/lib/text';

function LogoMark() {
  return (
    <img
      src={FullLogo}
      alt="ROI Agency"
      width={40}
      height={40}
      decoding="async"
      fetchPriority="high"
      className="h-8 w-8 md:h-10 md:w-10 object-contain"
    />
  );
}

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const { appData } = useAppData();
  const { language, setLanguage } = useLanguage();
  const isRTL = language === 'ar';

  const t = appData?.content?.nav?.[language] || {};

  const menuItems = useMemo(
    () => [
      { key: 'home', href: '#home', label: t.home || 'Home' },
      { key: 'services', href: '#services', label: t.services || 'Services' },
      { key: 'techProducts', href: '#tech-products', label: t.techProducts || 'Tech Products' },
      { key: 'work', href: '#portfolio', label: t.work || 'Work' },
      { key: 'about', href: '#about', label: t.about || 'About' },
      { key: 'team', href: '#team', label: t.team || 'Team' },
      { key: 'join', href: '#contact', label: t.join || 'Join' },
    ],
    [t.home, t.services, t.techProducts, t.work, t.about, t.team, t.join]
  );

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 16);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const closeMenu = () => setIsOpen(false);

  const labelOf = (raw) =>
    language === 'ar' ? raw : toTitleCase(raw);

  return (
    <>
      <header
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
          isScrolled || isOpen
            ? 'bg-dark/95 border-b border-primary/15 shadow-[0_8px_40px_rgba(0,0,0,0.45)]'
            : 'bg-transparent border-b border-transparent'
        }`}
      >
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div
            className={`flex h-16 md:h-20 items-center justify-between gap-4 ${
              isRTL ? 'flex-row-reverse' : ''
            }`}
          >
            {/* Brand — static PNG only (3D WebGL logo was a major desktop TBT cost) */}
            <a
              href="#home"
              aria-label="ROI Agency"
              className="relative z-[60] inline-flex items-center gap-2.5 shrink-0"
              onClick={closeMenu}
            >
              <LogoMark />
              <span className="hidden sm:block font-display text-sm font-bold uppercase tracking-[0.22em] text-white">
                ROI
              </span>
            </a>

            {/* Desktop links */}
            <nav
              className="hidden lg:flex items-center gap-1 xl:gap-2"
              aria-label="Main navigation"
            >
              {menuItems.map((item) => (
                <a
                  key={item.key}
                  href={item.href}
                  className="px-3 py-2 font-display text-[11px] xl:text-xs font-bold uppercase tracking-[0.18em] text-white/65 hover:text-primary transition-colors"
                >
                  {labelOf(item.label)}
                </a>
              ))}
            </nav>

            <div className={`flex items-center gap-2 sm:gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
              {/* Language */}
              <div
                className="flex items-center rounded-xl border border-primary/20 bg-dark-panel/80 p-1"
                role="group"
                aria-label="Language"
              >
                {['ar', 'en'].map((lang) => (
                  <button
                    key={lang}
                    type="button"
                    onClick={() => setLanguage(lang)}
                    aria-label={lang === 'ar' ? 'Switch to Arabic' : 'Switch to English'}
                    aria-pressed={language === lang}
                    className={`min-w-[40px] min-h-[36px] px-2.5 rounded-lg text-xs font-bold uppercase tracking-wider transition-all ${
                      language === lang
                        ? 'bg-primary text-dark shadow-glow-sm'
                        : 'text-white/50 hover:text-primary'
                    }`}
                  >
                    {lang}
                  </button>
                ))}
              </div>

              {/* Desktop CTA */}
              <a
                href="#contact"
                className="hidden md:inline-flex items-center justify-center min-h-[40px] px-4 rounded-xl bg-primary text-dark text-xs font-bold uppercase tracking-[0.16em] hover:bg-primary-hover transition-colors shadow-glow-sm"
              >
                {labelOf(t.join || 'Join')}
              </a>

              {/* Mobile toggle */}
              <button
                type="button"
                className="lg:hidden relative z-[60] inline-flex h-11 w-11 items-center justify-center rounded-xl border border-primary/25 bg-dark-panel text-white hover:border-primary hover:text-primary transition-colors"
                aria-label={isOpen ? 'Close menu' : 'Open menu'}
                aria-expanded={isOpen}
                aria-controls="mobile-nav"
                onClick={() => setIsOpen((v) => !v)}
              >
                {isOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile overlay */}
      <div
        id="mobile-nav"
        className={`fixed inset-0 z-40 lg:hidden transition-[opacity,visibility] duration-300 ${
          isOpen ? 'opacity-100 visible' : 'opacity-0 invisible pointer-events-none'
        }`}
        aria-hidden={!isOpen}
      >
        <button
          type="button"
          className="absolute inset-0 bg-dark/90"
          aria-label="Close menu"
          onClick={closeMenu}
        />

        <div
          className={`absolute inset-y-0 w-full max-w-md bg-dark shadow-lift transition-transform duration-300 ease-out ${
            isRTL
              ? `left-0 border-r border-primary/20 ${isOpen ? 'translate-x-0' : '-translate-x-full'}`
              : `right-0 border-l border-primary/20 ${isOpen ? 'translate-x-0' : 'translate-x-full'}`
          }`}
        >
          <div className="absolute inset-0 ig-grid opacity-40 pointer-events-none" aria-hidden="true" />
          <div
            className="neon-halo w-64 h-64 top-24 left-1/2 -translate-x-1/2 opacity-50"
            aria-hidden="true"
          />

          <div className="relative flex h-full flex-col px-6 pt-24 pb-8">
            <p className="font-display text-[11px] font-bold uppercase tracking-[0.35em] text-neon mb-8">
              {language === 'ar' ? 'القائمة' : 'Menu'}
            </p>

            <nav className="flex flex-col gap-1" aria-label="Mobile navigation">
              {menuItems.map((item, index) => (
                <a
                  key={item.key}
                  href={item.href}
                  onClick={closeMenu}
                  className={`group flex items-center justify-between border-b border-white/10 py-4 font-display text-2xl font-bold uppercase tracking-tight text-white hover:text-primary transition-colors ${
                    isRTL ? 'flex-row-reverse' : ''
                  }`}
                  style={{ transitionDelay: isOpen ? `${index * 40}ms` : '0ms' }}
                >
                  <span>{labelOf(item.label)}</span>
                  <span className="text-xs tabular-nums text-white/25 group-hover:text-primary/60">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                </a>
              ))}
            </nav>

            <div className="mt-auto pt-8">
              <a
                href="#contact"
                onClick={closeMenu}
                className="flex w-full items-center justify-center min-h-[52px] rounded-xl bg-primary text-dark font-bold uppercase tracking-[0.18em] text-sm shadow-glow hover:bg-primary-hover transition-colors"
              >
                {labelOf(t.join || 'Join')}
              </a>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Navbar;
